import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFps } from "@rapid/contexts/FpsContext";
import { useVideoSegmentation } from "@rapid/utils/videoSegmentation";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { TimelineInferenceProgressOverlay } from "./TimelineInferenceProgressOverlay";
import { TimelinePredictionMarkers } from "./TimelinePredictionMarkers";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { useVideoTimelinePreviews } from "@rapid/hooks/useVideoTimelinePreviews";
import { formatTime, ThumbnailMode } from "@rapid/utils/videoEditing";
import { PreviewThumbnails } from "../Preview/PreviewThumbnails";
import { MediaItem } from "@rapid/types/media";
import { useCurrentMedia, useSetCurrentMedia, useMediaStore } from "@rapid/store";

interface VideoControlsTimelineProps {
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    mediaItem?: MediaItem;
    flowLocked: boolean;
    onPauseClicked?: (currentTime: number) => void;
    onFramePreviewsReady?: () => void;
    currentTime: number;
    setCurrentTime: (time: number) => void;
    isVideoReadyForSeeking: boolean;
    setIsVideoReadyForSeeking: (ready: boolean) => void;
    onTimelineClick?: (targetTime: number) => void;
    isDragging: boolean;
    setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
    isPlaying?: boolean;
    hoverThrottleRef: React.RefObject<number | null>;
}

const SLIDER_GRANULARITY_MULTIPLIER = 10;

const THUMBNAIL_GENERATOR_OPTS = {
    size: 128,
    quality: 6,
    mode: ThumbnailMode.ACCURATE,
    numPreviews: 8,
    defaultFramePreviews: Array(8).fill(undefined)
};

export const VideoControlsTimeline: React.FC<VideoControlsTimelineProps> = ({
    videoRef,
    mediaItem,
    flowLocked,
    onPauseClicked,
    onFramePreviewsReady,
    currentTime,
    setCurrentTime,
    isVideoReadyForSeeking,
    setIsVideoReadyForSeeking,
    onTimelineClick,
    isDragging,
    setIsDragging,
    isPlaying = false,
    hoverThrottleRef
}) => {
    const { hasFullVideoInference, step } = useFlowState();
    const [hoverPositionX, setHoverPositionX] = useState<number | null>(null);
    const [isHoveringMarker, setIsHoveringMarker] = useState(false);

    const timelineAreaRef = useRef<HTMLDivElement>(null);
    const lastSeekTimeRef = useRef<number>(0); // Track last seek time to prevent conflicts

    // Use inference FPS for timeline calculations to match prediction segments
    // This ensures timeline clicks align with the FPS used during inference
    const { fps: inferenceFps } = useFps();
    const videoFps = inferenceFps || mediaItem?.metadata?.videoMetadata?.fps || 30;

    const totalFrames = useMemo(
        () => mediaItem?.metadata?.frame_count || 0,
        [mediaItem?.metadata?.frame_count]
    );
    const fileDuration = useMemo(
        () => mediaItem?.metadata?.videoMetadata?.duration || 0,
        [mediaItem?.metadata?.videoMetadata?.duration]
    );
    const keyframeOffset = useMemo(
        () => mediaItem?.metadata?.keyframe_offset || 0,
        [mediaItem?.metadata?.keyframe_offset]
    );

    // Use the custom hook for preview generation
    const { orderedFramePreviews } = useVideoTimelinePreviews({
        mediaItem,
        totalFrames,
        fileDuration,
        keyframeOffset,
        onFramePreviewsReady,
        isVideoReadyForSeeking,
        setIsVideoReadyForSeeking,
        videoRef,
        thumbnailGeneratorOpts: THUMBNAIL_GENERATOR_OPTS
    });

    const { secondsPerFrame, segmentForTargetTime, clampedTimeForSegment, frameCount } =
        useVideoSegmentation(fileDuration, videoFps);

    // Adjust frame calculations for keyframe offset
    // For trimmed videos, we need to calculate frames relative to the effective start time
    const adjustedCurrentTime = Math.max(0, currentTime - keyframeOffset);

    const currentGranularFrameForSlider = Math.min(
        Math.floor(adjustedCurrentTime * videoFps * SLIDER_GRANULARITY_MULTIPLIER),
        frameCount > 0 ? (frameCount - 1) * SLIDER_GRANULARITY_MULTIPLIER : 0
    );

    // Trimmed videos in Rapid are produced via stream-copy (fastMode) so they inherit the
    // source's sparse keyframes. The <video> element seeks to the nearest preceding keyframe,
    // which can be a full second before the time we asked for, leaving the playhead far from
    // the marker the user just clicked. Track the user's intended adjusted time and use it
    // for the visual playhead until playback resumes.
    const [intendedAdjustedTime, setIntendedAdjustedTime] = useState<number | null>(null);
    useEffect(() => {
        if (isPlaying) setIntendedAdjustedTime(null);
    }, [isPlaying]);

    const visualAdjustedTime = intendedAdjustedTime ?? adjustedCurrentTime;

    // Snap to the nearest frame so the playhead's percent matches the markers' integer-segment
    // grid (`(segment / fps) / duration * 100`). Rounding also absorbs sub-frame FP drift in
    // the timeupdate path for non-trimmed videos.
    const snappedTime = videoFps > 0 ? Math.round(visualAdjustedTime * videoFps) / videoFps : 0;
    const playheadPercent = fileDuration > 0 ? (snappedTime / fileDuration) * 100 : 0;

    const updateCurrentTimeFromTimelineInteraction = useCallback(
        (granularFrame: number) => {
            const segment = Math.round(granularFrame / SLIDER_GRANULARITY_MULTIPLIER);
            const relativeTime = clampedTimeForSegment(segment);
            // Add keyframe offset to get absolute time for video element
            const absoluteTime = relativeTime + keyframeOffset;

            if (videoRef?.current) videoRef.current.currentTime = absoluteTime;
            if (isDragging) setCurrentTime(absoluteTime);
            setIntendedAdjustedTime(relativeTime);
        },
        [clampedTimeForSegment, videoRef, isDragging, setCurrentTime, keyframeOffset]
    );

    const handleTimelineChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (flowLocked) return;

            updateCurrentTimeFromTimelineInteraction(parseInt(e.target.value, 10));
        },
        [flowLocked, updateCurrentTimeFromTimelineInteraction]
    );

    const handleTimelineInput = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            if (flowLocked) return;

            updateCurrentTimeFromTimelineInteraction(parseInt(e.currentTarget.value, 10));
        },
        [flowLocked, updateCurrentTimeFromTimelineInteraction]
    );

    const handleTimelineMouseDown = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(true);
    }, [flowLocked, setIsDragging]);

    const handleTimelineMouseUp = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(false);
        onPauseClicked?.(currentTime);
    }, [flowLocked, currentTime, onPauseClicked, setIsDragging]);

    const handleTimelineTouchStart = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(true);
    }, [flowLocked, setIsDragging]);

    const handleTimelineTouchEnd = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(false);
        onPauseClicked?.(currentTime);
    }, [flowLocked, currentTime, onPauseClicked, setIsDragging]);

    const handleTimelineMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (flowLocked) return;
            if (!timelineAreaRef.current || !fileDuration || fileDuration === 0) {
                setHoverPositionX(null);
                return;
            }
            if (hoverThrottleRef.current) cancelAnimationFrame(hoverThrottleRef.current);
            hoverThrottleRef.current = requestAnimationFrame(() => {
                const rect = timelineAreaRef.current?.getBoundingClientRect();
                if (!rect) return;
                const offsetX = e.clientX - rect.left;
                const clampedOffsetX = Math.max(0, Math.min(offsetX, rect.width));
                if (rect.width === 0) {
                    setHoverPositionX(null);
                    return;
                }
                const percent = (clampedOffsetX / rect.width) * 100;
                setHoverPositionX(percent);
            });
        },
        [flowLocked, fileDuration, hoverThrottleRef]
    );

    const handleTimelineMouseLeave = useCallback(() => {
        if (hoverThrottleRef.current) cancelAnimationFrame(hoverThrottleRef.current);
        hoverThrottleRef.current = null;
        setHoverPositionX(null);
    }, [hoverThrottleRef]);

    const onClick = useCallback(
        (clientX: number) => {
            if (!timelineAreaRef.current || !fileDuration || isDragging) return;
            if (!videoRef?.current) return;

            const rect = timelineAreaRef.current.getBoundingClientRect();
            const offsetX = clientX - rect.left;
            const clampedOffsetX = Math.max(0, Math.min(offsetX, rect.width));
            if (rect.width === 0) return;

            const percentage = clampedOffsetX / rect.width;
            // Calculate relative time within the trimmed video duration
            let relativeTime = percentage * fileDuration;
            relativeTime = Math.max(0, Math.min(relativeTime, fileDuration));

            const segment = segmentForTargetTime(relativeTime);
            const clampedRelativeTime = clampedTimeForSegment(segment);

            if (clampedRelativeTime === undefined) return;

            // Convert relative time to absolute time by adding keyframe offset
            const absoluteTime = clampedRelativeTime + keyframeOffset;
            setIntendedAdjustedTime(clampedRelativeTime);

            const video = videoRef.current;

            if (!isVideoReadyForSeeking) {
                video.muted = true;
                if (video.paused) {
                    video
                        .play()
                        .then(() => {
                            video.pause();
                            video.currentTime = absoluteTime;
                            setCurrentTime(absoluteTime);
                            setIsVideoReadyForSeeking(true);
                        })
                        .catch(() => {
                            video.currentTime = absoluteTime;
                            setCurrentTime(absoluteTime);
                            setIsVideoReadyForSeeking(true);
                        });
                } else {
                    video.currentTime = absoluteTime;
                    setCurrentTime(absoluteTime);
                    setIsVideoReadyForSeeking(true);
                }
            } else {
                video.currentTime = absoluteTime;
                setCurrentTime(absoluteTime);
                lastSeekTimeRef.current = Date.now();
                onTimelineClick?.(absoluteTime);
            }
        },
        [
            timelineAreaRef,
            fileDuration,
            isDragging,
            videoRef,
            segmentForTargetTime,
            clampedTimeForSegment,
            isVideoReadyForSeeking,
            setIsVideoReadyForSeeking,
            setCurrentTime,
            onTimelineClick,
            keyframeOffset
        ]
    );

    const handleTimelineClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (flowLocked) return;

            onClick(e.clientX);
        },
        [flowLocked, onClick]
    );

    const handleTimelineTouch = useCallback(
        (e: React.TouchEvent<HTMLDivElement>) => {
            if (flowLocked) return;

            const touch = e.touches[0] || e.changedTouches[0];
            if (!touch) return;

            onClick(touch.clientX);
        },
        [flowLocked, onClick]
    );

    const currentMedia = useCurrentMedia();
    const setCurrentMedia = useSetCurrentMedia();
    const media = useMediaStore();

    const handleMarkerClick = useCallback(
        (targetTime: number, segment: number) => {
            if (flowLocked || !videoRef?.current) return;
            videoRef.current.currentTime = targetTime;
            setCurrentTime(targetTime);
            setIntendedAdjustedTime(Math.max(0, targetTime - keyframeOffset));
            if (currentMedia?.file) {
                // Read the latest file from the media store to get up-to-date segmentToFrameMediaItemIdMap
                const latestFile = media[currentMedia.file.id] ?? currentMedia.file;
                const segmentFrameId = latestFile.segmentToFrameMediaItemIdMap?.[segment];
                const frameMediaItem = segmentFrameId ? media[segmentFrameId] : undefined;
                setCurrentMedia({ file: latestFile, frameSegment: segment, frame: frameMediaItem });
            }
        },
        [
            flowLocked,
            videoRef,
            setCurrentTime,
            keyframeOffset,
            currentMedia?.file,
            setCurrentMedia,
            media
        ]
    );

    return (
        <div
            ref={timelineAreaRef}
            onMouseMove={handleTimelineMouseMove}
            onMouseLeave={handleTimelineMouseLeave}
            onClick={handleTimelineClick}
            onTouchEnd={handleTimelineTouch}
            className={twClassNames(
                "videoTimeline relative",
                flowLocked ? "pointer-events-none cursor-not-allowed opacity-50" : "cursor-pointer"
            )}
        >
            <PreviewThumbnails previews={orderedFramePreviews} />

            {fileDuration > 0 && (
                <>
                    {/* Preview thumbnails */}
                    <div
                        className="timestampContainer top-0 z-50 -translate-y-full"
                        style={{
                            left: `${playheadPercent}%`,
                            transform: `translateX(-50%) translateY(-36px)`
                        }}
                    >
                        <span className="timestamp">{formatTime(currentTime)}</span>
                    </div>
                    {hasFullVideoInference && mediaItem && (
                        <TimelineInferenceProgressOverlay mediaItem={mediaItem} />
                    )}
                    {!hasFullVideoInference && mediaItem && (
                        <TimelinePredictionMarkers
                            mediaItem={mediaItem}
                            onMarkerClick={handleMarkerClick}
                            onMarkerHoverChange={setIsHoveringMarker}
                        />
                    )}
                    {hoverPositionX !== null && !isDragging && !isHoveringMarker && (
                        <div
                            className="cursorTimeIndicator pointer-events-none absolute bottom-0 top-0"
                            style={{ left: `${hoverPositionX}%`, transform: "translateX(-25%)" }}
                        />
                    )}
                    {/* Custom playhead — shares the markers' coordinate system (left: percent%
                        + translateX(-50%)) so they always align pixel-for-pixel. The native
                        slider thumb is kept transparent below; it still handles drag/keyboard.
                        Kept narrower than the marker line (w-1) so it doesn't occlude a marker
                        when the playhead lands on top of one. */}
                    <div
                        className="pointer-events-none absolute z-40 w-0.5 -translate-x-1/2 bg-gray-900 ring-[1px] ring-white/70"
                        style={{
                            left: `${playheadPercent}%`,
                            top: -16,
                            height: "calc(100% + 28px)"
                        }}
                    />
                    <input
                        type="range"
                        min={0}
                        max={frameCount > 0 ? (frameCount - 1) * SLIDER_GRANULARITY_MULTIPLIER : 0}
                        step={1}
                        value={currentGranularFrameForSlider}
                        onChange={handleTimelineChange}
                        onInput={handleTimelineInput}
                        onMouseDown={handleTimelineMouseDown}
                        onMouseUp={handleTimelineMouseUp}
                        onTouchStart={handleTimelineTouchStart}
                        onTouchEnd={handleTimelineTouchEnd}
                        style={{
                            WebkitTapHighlightColor: "transparent",
                            touchAction: "manipulation"
                        }}
                        className={twClassNames(
                            "group absolute inset-0 z-20 h-full w-full appearance-none rounded-lg bg-transparent focus:outline-none [&::-moz-range-thumb]:h-[calc(100%+16px)] [&::-moz-range-thumb]:w-1.5 [&::-moz-range-thumb]:translate-y-[-8px] [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:opacity-0 [&::-moz-range-track]:h-full [&::-moz-range-track]:appearance-none [&::-webkit-slider-runnable-track]:h-full [&::-webkit-slider-runnable-track]:appearance-none [&::-webkit-slider-thumb]:h-[calc(100%+16px)] [&::-webkit-slider-thumb]:w-1.5 [&::-webkit-slider-thumb]:translate-y-[-8px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:opacity-0",
                            flowLocked ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"
                        )}
                    />
                </>
            )}
        </div>
    );
};
